import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Pencil, Plus, Trash2, Trophy } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg, ghs, PREDICTION_CATEGORIES } from "../../lib/api";
import { EmptyState, PageHeader, StatusBadge } from "../../components/site/Bits";
import { Field } from "../public/Auth";
import { Modal, Table, Td, Toggle } from "./kit";

const BLANK = { name: "", category: "football", short_description: "", description: "", price: "", duration_days: 1, is_available: true, image_path: null };
const CAT = Object.fromEntries(PREDICTION_CATEGORIES.map((c) => [c.key, c.title]));

const PackageForm = ({ initial, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, price: Number(f.price), duration_days: Number(f.duration_days) };
    try {
      if (f.id) await api.put(`/admin/prediction-packages/${f.id}`, body); else await api.post("/admin/prediction-packages", body);
      toast.success("Package saved"); onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="package-form">
      <Field label="Package name"><input required minLength={2} value={f.name} onChange={set("name")} className="input-rx" data-testid="package-form-name" /></Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Section">
          <select value={f.category} onChange={set("category")} className="input-rx" data-testid="package-form-category">
            {PREDICTION_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
          </select>
        </Field>
        <Field label="Price (GH₵)"><input required type="number" min="0.01" step="0.01" value={f.price} onChange={set("price")} className="input-rx" data-testid="package-form-price" /></Field>
        <Field label="Access days"><input required type="number" min="1" max="365" value={f.duration_days} onChange={set("duration_days")} className="input-rx" data-testid="package-form-days" /></Field>
      </div>
      <Field label="Short description"><input maxLength={200} value={f.short_description} onChange={set("short_description")} className="input-rx" data-testid="package-form-short" /></Field>
      <Field label="Details"><textarea rows={4} value={f.description} onChange={set("description")} className="input-rx" data-testid="package-form-description" /></Field>
      <Toggle checked={f.is_available} onChange={(v) => setF({ ...f, is_available: v })} label="Visible to customers" testId="package-form-available" />
      <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="btn-ghost">Cancel</button><button className="btn-primary" data-testid="package-form-save">Save package</button></div>
    </form>
  );
};

const Packages = ({ filter }) => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/prediction-packages").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const list = filter ? items.filter((p) => p.category === filter) : items;
  const del = async (p) => { if (window.confirm(`Delete "${p.name}"?`)) { try { await api.delete(`/admin/prediction-packages/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } };
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><button onClick={() => setEdit({ ...BLANK, category: filter || "football" })} className="btn-primary" data-testid={`add-package-btn-${filter || "all"}`}><Plus size={16} /> Add package</button></div>
      {list.length === 0 ? <EmptyState icon={Trophy} testId={`packages-empty-${filter || "all"}`} title="No packages yet" text="Packages you create here appear in GET PREDICTIONS NOW. Prediction content is added later by the prediction system." /> : (
        <Table head={["Package", "Section", "Price", "Access", "Status", ""]} testId="admin-packages-table">
          {list.map((p) => (
            <tr key={p.id}>
              <Td className="font-medium text-white">{p.name}</Td><Td className="text-slate-400">{CAT[p.category]}</Td>
              <Td className="font-mono-rx text-amber-400">{ghs(p.price_minor)}</Td><Td className="text-slate-400">{p.duration_days}d</Td>
              <Td><StatusBadge status={p.is_available ? "active" : "cancelled"} label={p.is_available ? "Visible" : "Hidden"} /></Td>
              <Td className="text-right"><div className="flex justify-end gap-2">
                <button onClick={() => setEdit({ ...BLANK, ...p, price: (p.price_minor / 100).toFixed(2) })} className="rounded-lg p-2 text-slate-400 hover:text-white" data-testid={`package-edit-${p.id}`}><Pencil size={15} /></button>
                <button onClick={() => del(p)} className="rounded-lg p-2 text-slate-400 hover:text-red-300" data-testid={`package-delete-${p.id}`}><Trash2 size={15} /></button>
              </div></Td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit package" : "New prediction package"} testId="package-modal">
        {edit && <PackageForm initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </Modal>
    </div>
  );
};

const EngineEmpty = ({ what }) => (
  <EmptyState icon={Cpu} testId={`engine-empty-${what.toLowerCase().replace(/\s/g, "-")}`} title={`${what} — awaiting prediction system`}
    text="This module is structured and ready. It will be activated when the prediction system is installed. No data is generated until then." />
);

export default function AdminPredictions() {
  const tabs = [["packages", "Prediction Packages"], ["correct", "Correct-Score Packages"], ["products", "Prediction Products"], ["matches", "Matches"], ["predictions", "Predictions"], ["results", "Results"]];
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Predictions" text="Structure for the GET PREDICTIONS NOW section. Kept separate from the products store." />
      <Tabs defaultValue="packages">
        <TabsList className="no-scrollbar h-auto w-full justify-start gap-1 overflow-x-auto bg-white/[0.03] p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="whitespace-nowrap data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-200" data-testid={`pred-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="packages" className="mt-6"><Packages /></TabsContent>
        <TabsContent value="correct" className="mt-6"><Packages filter="correct_score" /></TabsContent>
        <TabsContent value="products" className="mt-6"><Packages filter="other" /></TabsContent>
        <TabsContent value="matches" className="mt-6"><EngineEmpty what="Match management" /></TabsContent>
        <TabsContent value="predictions" className="mt-6"><EngineEmpty what="Prediction management" /></TabsContent>
        <TabsContent value="results" className="mt-6"><EngineEmpty what="Results management" /></TabsContent>
      </Tabs>
    </div>
  );
}
